import React from 'react'
import { Stack, Button } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { makeStyles } from 'tss-react/mui'
import { REAUTHENTICATE_LOCAL_STORAGE_KEY } from '../../consts'
import { startReauthenticateVehicle } from '../../api'
import { useUserRoles } from 'src/Hooks/user'

const useStyles = makeStyles()(theme => ({
  reauthenticateVehicle: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),

    '& > *': {
      flex: 1,
    },

    [theme.breakpoints.up('sm')]: {
      width: 'initial',
      marginTop: theme.spacing(0),

      '& > *': {
        flex: 'initial',
      },
    },
  },

  removeVehicle: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: theme.spacing(1),

    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(0),
    },
  },
}))

interface Props {
  vehicleId: number
  installationId: string
  setIsRemoveVehicleModalOpen: (isOpen: boolean) => void
  hasVin: boolean
  isVehicleConnectionInProgress: boolean
  isVehicleDisconnected: boolean
}

/**
 * Component to handles vehicle actions:
 * - remove vehicle
 * - reauthenticate vehicle
 */
const VehicleTileActions: React.FC<Props> = ({
  vehicleId,
  installationId,
  setIsRemoveVehicleModalOpen,
  hasVin,
  isVehicleConnectionInProgress,
  isVehicleDisconnected,
}) => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()
  const { enqueueErrorStack } = useSnackbar()
  const { classes } = useStyles()
  const { isServiceUser } = useUserRoles()

  const handleRemoveVehicle = (): void => setIsRemoveVehicleModalOpen(true)

  const handleReauthenticateVehicle = async (): Promise<void> => {
    /* save vehicle id in local storage */
    localStorage.setItem(REAUTHENTICATE_LOCAL_STORAGE_KEY, vehicleId.toString())

    const { value, hasErrors } = await startReauthenticateVehicle(vehicleId, installationId, authFetch)

    if (hasErrors) {
      enqueueErrorStack('vehicle-integration.reauthentication.snackbar-failure-message')
      return
    }

    if (value) {
      window.open(value, '_self')
    }
  }

  const removeVehicleButton = (
    <Button
      variant="text"
      onClick={e => {
        e.stopPropagation()
        handleRemoveVehicle()
      }}
      sx={{ textTransform: 'uppercase' }}
    >
      {t('common.remove')}
    </Button>
  )

  const reconnectVehicleButton = (
    <Button
      disabled={isServiceUser}
      variant="contained"
      onClick={e => {
        e.stopPropagation()
        handleReauthenticateVehicle()
      }}
    >
      {t('emobility.vehicle-integration.reconnect')}
    </Button>
  )

  const canReauthenticateVehicle = isVehicleDisconnected && hasVin
  const cannotReauthenticateVehicle = isVehicleDisconnected && !hasVin

  if (canReauthenticateVehicle) {
    return (
      <Stack className={classes.reauthenticateVehicle}>
        {removeVehicleButton}
        {reconnectVehicleButton}
      </Stack>
    )
  }

  if (isVehicleConnectionInProgress || cannotReauthenticateVehicle) {
    return <Stack className={classes.removeVehicle}>{removeVehicleButton}</Stack>
  }
}

export default VehicleTileActions
